import React, { useState, useEffect, useMemo } from "react";
import { putTurno } from "../helpers/rutaTurnos";
import { getPersonas } from "../helpers/rutaPersonas";
import { Modal, Button } from "react-bootstrap";

/* ---------------- utils ---------------- */
const clean = (v) => String(v ?? "").trim();

const toDateInputValue = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const normalizePersonas = (r) =>
  Array.isArray(r?.personas)
    ? r.personas
    : Array.isArray(r)
    ? r
    : [];

const ATENCION_OPTS = [
  { value: 0, label: "SIN DEFINIR" },
  { value: 1, label: "EN ESPERA" },
  { value: 2, label: "ATENDIDO" },
  { value: 3, label: "RECHAZADO" },
];

const ModalFormTurno = ({ turno, handleClose }) => {
  const initial = useMemo(() => {
    const src = turno?.turno || turno?.turnos || turno || {};

    return {
      _id: src._id || "",
      codtur: src.codtur || "",
      fechaTurno: toDateInputValue(src.fechaTurno),
      persona:
        typeof src.persona === "object"
          ? src.persona?._id || ""
          : src.persona || "",
      atencion:
        src.atencion !== undefined && src.atencion !== null
          ? String(src.atencion)
          : "0",
      nombre: src.nombre || "",
      telefono: src.telefono || "",
      observacion: src.observacion || "",
      activo: typeof src.activo === "boolean" ? src.activo : true,
    };
  }, [turno]);

  const [formValues, setFormValues] = useState(initial);

  useEffect(() => {
    setFormValues(initial);
  }, [initial]);

  const [personas, setPersonas] = useState({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const resp = await getPersonas({ activo: true });
        const data = normalizePersonas(resp);

        if (alive) {
          setPersonas({
            data,
            loading: false,
            error: null,
          });
        }
      } catch (e) {
        if (alive) {
          setPersonas({
            data: [],
            loading: false,
            error: e?.message || "Error al cargar personas",
          });
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!formValues._id) throw new Error("Falta el ID del turno");
      if (!clean(formValues.fechaTurno)) {
        throw new Error("La fecha del turno es obligatoria");
      }
      if (!clean(formValues.persona)) {
        throw new Error("Debés seleccionar una persona");
      }
      if (!clean(formValues.nombre)) {
        throw new Error("El nombre es obligatorio");
      }

      const payload = {
        fechaTurno: formValues.fechaTurno,
        persona: formValues.persona,
        atencion: Number(formValues.atencion || 0),
        nombre: clean(formValues.nombre),
        telefono: clean(formValues.telefono),
        observacion: clean(formValues.observacion),
        activo: formValues.activo,
      };

      const resp = await putTurno(formValues._id, payload);
      console.log("Turno actualizado:", resp);
      handleClose();
    } catch (err) {
      console.error("Error actualizando turno:", err);
      alert(err?.message || "No se pudo actualizar el turno");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Modal.Body>
        <div className="form-row">
          <div className="form-group mt-2 col-sm-2">
            <label>Código</label>
            <input
              type="text"
              className="form-control"
              value={formValues.codtur}
              disabled
            />
          </div>

          <div className="form-group mt-2 col-sm-4">
            <label>Fecha Turno</label>
            <input
              type="date"
              className="form-control"
              name="fechaTurno"
              required
              value={formValues.fechaTurno}
              onChange={handleChange}
            />
          </div>

          <div className="form-group mt-2 col-sm-4">
            <label>Atención</label>
            <select
              className="form-control"
              name="atencion"
              value={formValues.atencion}
              onChange={handleChange}
              required
            >
              {ATENCION_OPTS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value} - {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group mt-3 col-sm-6">
            <label>Persona</label>
            <select
              className="form-control"
              name="persona"
              value={formValues.persona}
              onChange={handleChange}
              required
            >
              <option value="">Elija opción</option>
              {!personas.loading &&
                personas.data.map((p) => (
                  <option key={p._id} value={p._id}>
                    {(p.codper ?? "") !== "" ? `${p.codper} - ` : ""}
                    {p.nombre || "Sin nombre"}
                  </option>
                ))}
            </select>
            {personas.error && (
              <small className="text-danger">{personas.error}</small>
            )}
          </div>

          <div className="form-group mt-3 col-sm-6">
            <label>Nombre</label>
            <input
              type="text"
              className="form-control"
              name="nombre"
              maxLength={120}
              required
              value={formValues.nombre}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group mt-3 col-sm-4">
            <label>Teléfono</label>
            <input
              type="text"
              className="form-control"
              name="telefono"
              maxLength={40}
              value={formValues.telefono}
              onChange={handleChange}
            />
          </div>

          <div className="form-group mt-4 col-sm-3 d-flex align-items-center">
            <div className="form-check">
              <input
                id="chkActivoTurno"
                className="form-check-input"
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="chkActivoTurno">
                Activo
              </label>
            </div>
          </div>
        </div>

        <div className="form-group mt-3">
          <label>Observación</label>
          <textarea
            className="form-control"
            name="observacion"
            rows={4}
            value={formValues.observacion}
            onChange={handleChange}
            style={{
              fontSize: "1.1rem",
              lineHeight: "1.5",
              minHeight: "120px",
            }}
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancelar
        </Button>
        <Button type="submit" variant="dark">
          Guardar
        </Button>
      </Modal.Footer>
    </form>
  );
};

export default ModalFormTurno;